import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Headphones } from 'lucide-react';
import { motion } from 'framer-motion';
import { categories, type BlogPost } from '../../data/blog';

/**
 * BLOCK: journal_carousel
 * ACF layout name: `block_journal_carousel`
 *
 * Fields:
 *  - eyebrow      (text)
 *  - heading      (text)
 *  - lead         (text — the large italic line)
 *  - body         (textarea)
 *  - cta          (group: label, link)
 *  - posts        (relationship → Post CPT; empty = 3 most recent)
 *
 * Used on: Home ("Stay inspired between sessions").
 */

interface JournalCarouselProps {
  eyebrow?: string;
  heading: string;
  lead?: string;
  body?: string;
  ctaLabel: string;
  ctaTo: string;
  posts: readonly BlogPost[];
}
function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
}
export function JournalCarousel({
  eyebrow = 'The Garden',
  heading,
  lead,
  body,
  ctaLabel,
  ctaTo,
  posts
}: JournalCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    carouselRef.current?.scrollBy({
      left: direction === 'right' ? 320 : -320,
      behavior: 'smooth'
    });
  };
  return <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center" data-id="element-81" data-mp="17w6p0p4apdfe">
      <motion.div initial={{
      opacity: 0,
      x: -18
    }} whileInView={{
      opacity: 1,
      x: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} data-id="element-82" data-mp="1vlr8ra89wepq">
        <div className="flex items-center gap-3 text-rose-700" data-id="element-83" data-mp="1mj9mivsmbm2q">
          <span aria-hidden="true" className="h-px w-10 bg-rose-700/50" data-id="element-84" data-mp="1ql3jaszp05xm" />
          <Headphones size={16} strokeWidth={1.4} aria-hidden="true" data-id="element-85" data-mp="1qnhdkzmjte44" />
        </div>
        <p className="mt-5 text-xs font-medium tracking-[0.26em] text-rose-700 uppercase" data-id="element-86" data-mp="b6z1xco12w9d">
          {eyebrow}
        </p>
        <h2 className="mt-4 font-serif text-4xl leading-tight text-charcoal sm:text-5xl" data-id="element-87" data-mp="2ht51m9nnosym">
          {heading}
        </h2>
        {lead && <p className="mt-6 font-serif text-2xl italic leading-snug text-charcoal" data-id="element-88" data-mp="2cvd0cp14fuhr">
            {lead}
          </p>}
        {body && <p className="mt-5 max-w-md text-base font-light leading-relaxed text-charcoal-50" data-id="element-89" data-mp="280z46kemxjc1">
            {body}
          </p>}
        <Link to={ctaTo} className="group mt-8 inline-flex items-center gap-3 border-b border-charcoal pb-1 text-xs font-medium tracking-[0.16em] text-charcoal uppercase transition-colors hover:border-rose-700 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-700 focus:ring-offset-4" data-id="element-90" data-mp="1z2x2s3qzooo8">
          {ctaLabel}
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-91" data-mp="1k7qx5hvt2rvf" />
        </Link>
      </motion.div>

      <div className="min-w-0" data-id="element-92" data-mp="3n0o39q65c11l">
        <div className="mb-5 hidden items-center justify-end gap-2 lg:flex" data-id="element-93" data-mp="3mzcybn0ysn8w">
          <button type="button" onClick={() => scroll('left')} aria-label="Previous journal posts" className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-700/40 text-rose-700 transition-colors hover:border-rose-700 hover:bg-rose-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-700 focus:ring-offset-2 focus:ring-offset-rose-50" data-id="element-94" data-mp="3r1hq0g7ur9sw">
            <ChevronLeft size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-95" data-mp="383nk4b20teeq" />
          </button>
          <button type="button" onClick={() => scroll('right')} aria-label="Next journal posts" className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-700/40 text-rose-700 transition-colors hover:border-rose-700 hover:bg-rose-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-700 focus:ring-offset-2 focus:ring-offset-rose-50" data-id="element-96" data-mp="3d0nidzsmpcva">
            <ChevronRight size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-97" data-mp="218xxxdh3tnc3" />
          </button>
        </div>

        <div ref={carouselRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-5 pr-6 [scrollbar-color:#A36B6B_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-rose-700/60 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5" aria-label="Latest journal posts" role="region" tabIndex={0} data-id="element-98" data-mp="2f8pffo6txluu">
          {posts.map((post, index) => {
          const category = categories.find(item => item.id === post.category);
          return <motion.article key={post.slug} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.45,
            delay: index * 0.07
          }} className="w-[82vw] max-w-[290px] shrink-0 snap-start bg-white shadow-soft sm:w-[300px]" data-id="element-99" data-mp="37x4uubtvi66p" data-mpi={index}>
                <Link to={`/journal/${post.slug}`} className="group flex h-full flex-col focus:outline-none focus:ring-2 focus:ring-rose-700 focus:ring-offset-4" data-id="element-100" data-mp="3cmfeoga0zylu">
                  <div className="aspect-[4/3] overflow-hidden bg-cream-200" data-id="element-101" data-mp="33skihxqvtiic">
                    <img src={post.image} alt={post.title} loading="lazy" decoding="async" width={600} height={450} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-102" data-mp="38ku5p60m44gx" />
                  </div>
                  <div className="flex flex-1 flex-col p-6" data-id="element-103" data-mp="2owl11whvu60j">
                    <p className="text-[10px] font-medium tracking-[0.2em] text-rose-700 uppercase" data-id="element-104" data-mp="2oy5vlm475oye">
                      {category?.name ?? 'Journal'} · {formatDate(post.date)}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal transition-colors group-hover:text-rose-700" data-id="element-105" data-mp="lnst2aeoe4ir">
                      {post.title}
                    </h3>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium tracking-[0.12em] text-sage-700 uppercase" data-id="element-106" data-mp="gmvdm5l1voky">
                      Read reflection
                      <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-107" data-mp="19e6n26gtmdfz" />
                    </span>
                  </div>
                </Link>
              </motion.article>;
        })}
        </div>
      </div>
    </div>;
}